// GroupSelectionPage.jsx - Moderne groep selectie na login
import { motion } from 'framer-motion';
import { MapPin, Users, Home, ChevronRight, Building2, Sparkles } from 'lucide-react';

const GroupCard = ({ group, onClick, index }) => {
  return (
    <motion.button
      initial={{ opacity: 0, y: 30, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay: index * 0.15, duration: 0.5, type: "spring" }}
      whileHover={{ scale: 1.02, y: -4 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className="w-full p-6 rounded-3xl bg-white/90 backdrop-blur-xl border border-white/50 shadow-xl hover:shadow-2xl transition-all duration-300 group"
      data-testid={`group-card-${group.id}`}
    >
      <div className="flex items-center gap-4">
        {/* Icon */}
        <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${group.gradient} flex items-center justify-center shadow-lg ${group.shadowColor}`}>
          <group.icon className="w-8 h-8 text-white" strokeWidth={1.8} />
        </div>
        
        {/* Content */}
        <div className="flex-1 text-left">
          <h3 className="text-xl font-bold text-slate-800 mb-1">{group.name}</h3>
          <div className="flex items-center gap-2 text-slate-500">
            <MapPin className="w-4 h-4" />
            <span className="text-sm">{group.address}</span>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <Users className="w-4 h-4 text-slate-400" />
            <span className="text-xs text-slate-400">{group.clientCount} cliënten</span>
          </div>
        </div>
        
        {/* Arrow */}
        <div className="w-10 h-10 rounded-full bg-slate-100 group-hover:bg-slate-200 flex items-center justify-center transition-colors">
          <ChevronRight className="w-5 h-5 text-slate-600 group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </motion.button>
  );
};

const GROUPS = [
  {
    id: 'groep-zwaluw',
    name: 'Groep Zwaluw',
    address: 'Enschotsestraat 98 & 100',
    icon: Home,
    gradient: 'from-emerald-400 via-green-500 to-teal-500',
    shadowColor: 'shadow-emerald-500/30',
    clientCount: 5,
  },
  {
    id: 'groep-adelaars',
    name: 'Groep Adelaars',
    address: 'Ardennenlaan 68',
    icon: Building2,
    gradient: 'from-blue-400 via-indigo-500 to-purple-500',
    shadowColor: 'shadow-blue-500/30',
    clientCount: 5,
  },
  {
    id: 'ambulant-roodborst',
    name: 'Ambulant Roodborst',
    address: 'Statenlaan 59',
    icon: Home,
    gradient: 'from-orange-400 via-red-500 to-rose-500',
    shadowColor: 'shadow-orange-500/30',
    clientCount: 1,
  },
  {
    id: 'ambulant-ijsvogel',
    name: 'Ambulant IJsvogel',
    address: 'Piusplein 62-31',
    icon: Home,
    gradient: 'from-cyan-400 via-sky-500 to-blue-500',
    shadowColor: 'shadow-cyan-500/30',
    clientCount: 1,
  },
];

export const GroupSelectionPage = ({ user, onSelectGroup }) => {
  const firstName = user?.name?.split(' ')[0] || 'Begeleider';
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50/50 to-indigo-100/50 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{ 
            x: [0, 30, 0],
            y: [0, -20, 0],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-20 right-20 w-96 h-96 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ 
            x: [0, -40, 0],
            y: [0, 30, 0],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-20 left-10 w-80 h-80 bg-gradient-to-br from-emerald-400/20 to-teal-400/20 rounded-full blur-3xl"
        />
      </div>

      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          {/* Logo */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center shadow-xl shadow-indigo-500/30"
          >
            <Sparkles className="w-10 h-10 text-white" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-3xl sm:text-4xl font-bold text-slate-800 mb-2"
          >
            Welkom terug, <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">{firstName}</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-slate-500 text-lg"
          >
            Kies je woongroep om te beginnen
          </motion.p>
        </motion.div>

        {/* Group Cards */}
        <div className="w-full max-w-lg space-y-4">
          {GROUPS.map((group, index) => (
            <GroupCard
              key={group.id}
              group={group}
              index={index}
              onClick={() => onSelectGroup(group.id)}
            />
          ))}
        </div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-12 text-center"
        >
          <p className="text-sm text-slate-400">
            Zorg-Vleugel B.V. • Begeleid Wonen Dashboard
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default GroupSelectionPage;
